import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getBillingReport, updateMaterialCost, downloadBillingReportPdf,
} from '../api/client'

const MONTHS_ES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

function defaultYearMonth() {
  const now = new Date()
  let y = now.getFullYear()
  let m = now.getMonth() // 0-11; we want LAST completed month, so this index = previous month
  if (m === 0) { m = 12; y -= 1 } // 0 means January -> previous = December last year
  return { year: y, month: m }
}

export default function BillingReportModal({ onClose }) {
  const { t } = useTranslation(['purchases', 'common'])
  const init = defaultYearMonth()
  const [year, setYear] = useState(init.year)
  const [month, setMonth] = useState(init.month)
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [savingProjectId, setSavingProjectId] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [editVal, setEditVal] = useState('')
  const [editingProjectId, setEditingProjectId] = useState(null)

  const load = async () => {
    setLoading(true)
    try { setReport(await getBillingReport(year, month)) } catch { setReport(null) }
    setLoading(false)
  }

  useEffect(() => { load() }, [year, month])

  const handleMaterialEdit = async (project) => {
    if (editingProjectId !== project.project_id) return
    const trimmed = editVal.trim()
    if (trimmed === '') { setEditingProjectId(null); setEditVal(''); return }
    const amount = parseFloat(trimmed)
    if (!Number.isFinite(amount) || amount < 0) return
    setSavingProjectId(project.project_id)
    try {
      await updateMaterialCost(project.project_id, year, month, amount)
      await load()
    } catch (err) {
      window.alert(t('billingReport.failedSave') + ' ' + (err?.response?.data?.detail || t('billingReport.tryAgain')))
    }
    setEditingProjectId(null)
    setEditVal('')
    setSavingProjectId(null)
  }

  const startEdit = (project) => {
    setEditingProjectId(project.project_id)
    setEditVal(project.materiales_usd ? String(project.materiales_usd) : '')
  }

  const handleDownload = async () => {
    setDownloading(true)
    try { await downloadBillingReportPdf(year, month) }
    catch (err) {
      window.alert(t('billingReport.failedDownload') + ' ' + (err?.response?.data?.detail || t('billingReport.tryAgain')))
    }
    setDownloading(false)
  }

  const monthLabel = MONTHS_ES[month] || ''
  const years = [init.year - 1, init.year, init.year + 1]
  const fmt = (n) => `$${(n || 0).toLocaleString()}`

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">{t('billingReport.title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3 flex-wrap">
          <select
            value={year}
            onChange={e => setYear(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select
            value={month}
            onChange={e => setMonth(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            {MONTHS_ES.slice(1).map((label, i) => (
              <option key={i + 1} value={i + 1}>{label}</option>
            ))}
          </select>
          <span className="text-sm text-gray-400">{t('billingReport.hoursNote', { month: monthLabel, year })}</span>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-gray-400 text-sm">{t('common:actions.loading')}</div>
          ) : !report ? (
            <div className="text-red-600 text-sm">{t('billingReport.failedLoad')}</div>
          ) : (
            <>
              {report.projects_without_category?.length > 0 && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
                  <strong>{t('billingReport.missingCategory')}</strong> {t('billingReport.missingCategoryNote')}{' '}
                  {report.projects_without_category.join(', ')}.
                  {' '}{t('billingReport.setCategoryHint')}
                </div>
              )}

              {report.projects.length === 0 ? (
                <div className="text-center py-12 text-gray-400 text-sm">
                  {t('billingReport.noProjects', { month: monthLabel, year })}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <th className="text-left font-semibold px-3 py-2">{t('billingReport.cols.code')}</th>
                      <th className="text-left font-semibold px-3 py-2">{t('billingReport.cols.project')}</th>
                      <th className="text-left font-semibold px-3 py-2">{t('billingReport.cols.category')}</th>
                      <th className="text-right font-semibold px-3 py-2">{t('billingReport.cols.hours')}</th>
                      <th className="text-right font-semibold px-3 py-2">{t('billingReport.cols.costHr')}</th>
                      <th className="text-right font-semibold px-3 py-2">{t('billingReport.cols.materials')}</th>
                      <th className="text-right font-semibold px-3 py-2">{t('billingReport.cols.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.projects.map((p) => {
                      const isEditing = editingProjectId === p.project_id
                      const saving = savingProjectId === p.project_id
                      return (
                        <tr key={p.project_id} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-mono text-xs text-gray-500">{p.code || '—'}</td>
                          <td className="px-3 py-2 text-gray-800">{p.name}</td>
                          <td className="px-3 py-2 text-gray-600">{p.category}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{p.horas}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(p.costo_hr_usd)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {isEditing ? (
                              <input
                                autoFocus
                                type="number"
                                step="0.01"
                                min="0"
                                value={editVal}
                                onChange={e => setEditVal(e.target.value)}
                                onBlur={() => handleMaterialEdit(p)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleMaterialEdit(p)
                                  if (e.key === 'Escape') { setEditingProjectId(null); setEditVal('') }
                                }}
                                className="w-28 text-right border border-gray-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                              />
                            ) : (
                              <button
                                onClick={() => startEdit(p)}
                                disabled={saving}
                                className="hover:bg-gray-100 rounded px-2 py-1 -mx-2 -my-1"
                              >
                                {p.materiales_usd > 0 ? fmt(p.materiales_usd) : <span className="text-gray-300">{t('billingReport.addMaterial')}</span>}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(p.total_usd)}</td>
                        </tr>
                      )
                    })}
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan={3} className="px-3 py-2 text-right text-gray-700">{t('billingReport.totalRow')}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{report.projects.reduce((s, p) => s + p.horas, 0).toFixed(0)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(report.projects.reduce((s, p) => s + p.costo_hr_usd, 0))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(report.projects.reduce((s, p) => s + p.materiales_usd, 0))}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(report.grand_total_usd)}</td>
                    </tr>
                  </tbody>
                </table>
              )}

              {report.missing_rates?.length > 0 && (
                <p className="mt-3 text-xs italic text-gray-400">
                  {t('billingReport.missingRates', { names: report.missing_rates.join(', ') })}
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            {t('common:actions.close')}
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading || !report || report.projects.length === 0}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {downloading ? t('billingReport.generating') : t('billingReport.generatePdf')}
          </button>
        </div>
      </div>
    </div>
  )
}
